import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Deposit } from '@/features/isolated-portfolio/logic/useMyPortfolio'

import { Borrow } from '@/features/isolated-portfolio/logic/useMyPortfolio'
import { GroupedByPairAddress, TokenData } from './types'
import { PairDetails } from '@/features/isolated/logic/useIsolatedPairs'
import { IsolatedPairHealthInfo } from '@/features/isolated-details/logic/useIsolatedHealthFactor'
import { TokenWithBalance } from '@/domain/common/types'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { SpotBalance, SpotToken } from '@nktkas/hyperliquid'
import { Token } from '@/domain/types/Token'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { zeroAddress } from 'viem'
import { HyperCoreAsset } from '@/domain/wallet/hypercore/querySpotMeta'
import { MarketInfo } from '@/domain/market-info/marketInfo'
import { NativeAssetInfo } from '@/config/chain/types'
import { TokensInfo } from '@/domain/wallet/useTokens/TokenInfo'
import { TokenConfig } from '@/domain/wallet/useTokens/types'
import { MarketWalletInfo } from '@/domain/wallet/useMarketWalletInfo'
import { getChainConfigEntry } from '@/config/chain'
import { raise } from '@/utils/assert'
import { BigNumber } from 'bignumber.js'

export const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

export const usdFormatterCompact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 2,
})

export function getTotalIsolatedUSD(isolatedBorrows: Borrow[], isolatedDeposits: Deposit[]): NormalizedUnitNumber {
  const totalDepositsUSD = isolatedDeposits.reduce(
    (sum, deposit) => NormalizedUnitNumber(sum.plus(deposit.collateralToken.toUSD(deposit.lendAmount))),
    NormalizedUnitNumber(0),
  )

  const totalBorrowsUSD = isolatedBorrows.reduce(
    (sum, borrow) => NormalizedUnitNumber(sum.plus(borrow.borrowToken.toUSD(borrow.borrowAmount))),
    NormalizedUnitNumber(0),
  )

  return NormalizedUnitNumber(totalDepositsUSD.minus(totalBorrowsUSD))
}

export function getGroupedByPairAddress({
  pairs,
  isolatedDeposits,
  isolatedBorrows,
  pairsWithHealth,
}: {
  pairs: PairDetails[]
  isolatedDeposits: Deposit[]
  isolatedBorrows: Borrow[]
  pairsWithHealth: IsolatedPairHealthInfo[]
}): GroupedByPairAddress {
  return pairs
    .filter(
      (pair) =>
        isolatedDeposits.some((deposit) => deposit.pairAddress === pair.address) ||
        isolatedBorrows.some((borrow) => borrow.pairAddress === pair.address),
    )
    .reduce((acc, pair) => {
      acc[pair.address] = {
        name: `${pair.collateralToken.symbol} - ${pair.borrowToken.symbol} pool`,
        deposits: isolatedDeposits.filter((deposit) => deposit.pairAddress === pair.address),
        borrows: isolatedBorrows.filter((borrow) => borrow.pairAddress === pair.address),
        healthInfo: pairsWithHealth.find((p) => p.pairAddress === pair.address),
      }
      return acc
    }, {} as GroupedByPairAddress)
}

function renameToken(chainId: number, asset: TokenWithBalance): TokenWithBalance {
  const { tokenSymbolToReplacedName } =
    getChainConfigEntry(chainId).markets ?? raise('Markets config is not defined on this chain')

  if (!Object.keys(tokenSymbolToReplacedName).includes(asset.token.symbol)) {
    return asset
  }

  return {
    ...asset,
    token: new Token({
      ...asset.token,
      symbol: tokenSymbolToReplacedName[asset.token.symbol]!.symbol,
      name: tokenSymbolToReplacedName[asset.token.symbol]!.name,
      unitPriceUsd: asset.token.unitPriceUsd.toString(),
    }),
  }
}

export function formatTokenData({
  walletTokens,
  formattedL1Balances,
  chainId,
  nativeAssetInfo,
}: {
  walletTokens: TokenWithBalance[]
  formattedL1Balances: TokenWithBalance[]
  chainId: number
  nativeAssetInfo: NativeAssetInfo
}): Omit<TokenData, 'totalBalanceUSD' | 'totalUSD'> {
  const wrappedAsset = walletTokens.find((t) => t.token.symbol === nativeAssetInfo.wrappedNativeAssetSymbol)

  const filteredWalletTokens = walletTokens
    .reduce((acc, token) => {
      // Combine HYPE and WHYPE balances
      if (token.token.symbol === nativeAssetInfo.nativeAssetSymbol) {
        token.balance = NormalizedUnitNumber(token.balance.plus(wrappedAsset?.balance ?? 0))
      }

      // Only include tokens with a positive balance and not the wrapped native asset
      if (token.balance.gt(0) && token.token.symbol !== nativeAssetInfo.wrappedNativeAssetSymbol) {
        const existingToken = acc.find((t) => t.token.symbol === token.token.symbol)
        if (!existingToken) {
          acc.push(token)
        } else if (token.token.unitPriceUsd.gt(0)) {
          // Replace with the token that has a positive unitPriceUsd
          acc = acc.filter((t) => t.token.symbol !== token.token.symbol)
          acc.push(token)
        }
      }
      return acc
    }, [] as TokenWithBalance[])
    .sort((a, b) => b.token.toUSD(b.balance).minus(a.token.toUSD(a.balance)).toNumber())

  const sortedL1Balances = formattedL1Balances.sort((a, b) =>
    b.token.toUSD(b.balance).minus(a.token.toUSD(a.balance)).toNumber(),
  )

  const formattedTokens = {
    supported: {
      evm: [] as TokenWithBalance[],
      l1: [] as TokenWithBalance[],
    },
    otherTokens: {
      evm: [] as TokenWithBalance[],
      l1: [] as TokenWithBalance[],
    },
  }

  for (const asset of filteredWalletTokens) {
    const category = asset.token.unitPriceUsd.gt(0) ? 'supported' : 'otherTokens'
    formattedTokens[category].evm.push(renameToken(chainId, asset))
  }

  for (const asset of sortedL1Balances) {
    const category = asset.token.unitPriceUsd.gt(0) ? 'supported' : 'otherTokens'
    formattedTokens[category].l1.push(renameToken(chainId, asset))
  }
  return formattedTokens
}

export function formatL1Tokens({
  l1Balances,
  hyperCoreAssets,
  marketInfo,
  nativeAssetInfo,
}: {
  l1Balances: SpotBalance[]
  hyperCoreAssets: HyperCoreAsset[]
  marketInfo: MarketInfo
  nativeAssetInfo: NativeAssetInfo
}): TokenWithBalance[] {
  return l1Balances
    .filter((t) => Number(t.total) > 0)
    .map((t) => {
      const tokenInfo = hyperCoreAssets.find((a) => a.name === t.coin)
      const decimals = (tokenInfo?.weiDecimals ?? 5) + (tokenInfo?.evmContract?.evm_extra_wei_decimals ?? 13)
      const tokenSymbol = TokenSymbol(t.coin)

      const token = new Token({
        symbol: tokenSymbol,
        address: CheckedAddress(tokenInfo?.evmContract?.address || zeroAddress),
        name: tokenInfo?.fullName ?? t.coin,
        decimals,
        unitPriceUsd:
          [...marketInfo.reserves, marketInfo.findOneReserveBySymbol(nativeAssetInfo.nativeAssetSymbol)]
            .find((r) => r.token.symbol === tokenSymbol)
            ?.priceInUSD.toString() ?? '0',
      })
      return {
        token,
        balance: NormalizedUnitNumber(t.total),
      }
    })
}

export function formatExtraTokens({
  tokensInfo,
  pairs,
}: {
  tokensInfo: TokensInfo
  pairs: PairDetails[]
}): TokenWithBalance[] {
  return tokensInfo
    .filter((t) => t.balance.gt(0))
    .map((t) => {
      if (!t.token.unitPriceUsd.eq(0)) return t

      const tokenAddress = t.token.address.toLowerCase()

      const pair = pairs.find((p) => p.collateralToken.address.toLowerCase() === tokenAddress)
      const collateralPrice = pair?.collateralToken.unitPriceUsd

      return {
        ...t,
        token: new Token({
          ...t.token,
          unitPriceUsd: collateralPrice ? collateralPrice.toString() : '0',
        }),
      }
    })
}

export function formatTokenInfoAssets({
  hyperCoreAssets,
  nativeAssetInfo,
  walletInfo,
  isolatedPairs,
  isLoading,
}: {
  hyperCoreAssets: SpotToken[]
  nativeAssetInfo: NativeAssetInfo
  walletInfo: MarketWalletInfo
  isolatedPairs: PairDetails[]
  isLoading: boolean
}): TokenConfig[] {
  if (isLoading) return []

  const addressSet = new Set<string>()

  for (const { name, evmContract } of hyperCoreAssets) {
    if (
      evmContract?.address &&
      name !== nativeAssetInfo.nativeAssetSymbol &&
      !walletInfo.walletBalances.find((b) => b.token.address.toLowerCase() === evmContract.address.toLowerCase())
    ) {
      addressSet.add(evmContract.address.toLowerCase())
    }
  }

  for (const pair of isolatedPairs) {
    addressSet.add(pair.collateralToken.address.toLowerCase())
    addressSet.add(pair.borrowToken.address.toLowerCase())
  }

  const allConfigs: TokenConfig[] = Array.from(addressSet).map((address) => ({
    address: CheckedAddress(address),
    oracleType: 'zero-price',
  }))

  return allConfigs
}

export function getLowestHealthFactor(
  pooledHealthFactor: BigNumber | undefined,
  isolatedMinHealthFactor: BigNumber | undefined,
): BigNumber | undefined {
  if (typeof pooledHealthFactor === 'undefined' && typeof isolatedMinHealthFactor === 'undefined') {
    return undefined
  }

  if (typeof pooledHealthFactor === 'undefined') {
    return isolatedMinHealthFactor
  }

  if (typeof isolatedMinHealthFactor === 'undefined') {
    return pooledHealthFactor
  }

  return pooledHealthFactor.lt(isolatedMinHealthFactor) ? pooledHealthFactor : isolatedMinHealthFactor
}
